/* iter55p15 — Admin panel: Author Rankings.
 *
 * Displays the last N days of ticket-rail clicks + author-page views
 * per pen_slug, with an "engagement" score (3·clicks + views) that
 * drives the auto-rotation. Ops can eyeball the ranking before
 * pinning a new Featured author, or trigger the rotation manually
 * via the "Rotate now" button.
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, RefreshCcw, Award, Play } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { openPrompt } from '@/components/admin/ConfirmDialog';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

export default function AuthorRankingsPanel() {
  const [rows, setRows] = useState([]);
  const [days, setDays] = useState(7);
  const [busy, setBusy] = useState(false);
  const [rotating, setRotating] = useState(false);
  const [pinned, setPinned] = useState('');

  const adminHeaders = () => {
    const t = window.localStorage.getItem('adminToken');
    return t ? { Authorization: `Bearer ${t}` } : {};
  };

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const [digest, featured] = await Promise.all([
        axios.get(`${API}/admin/analytics/authors/weekly-digest?days=${days}`, { headers: adminHeaders() }),
        axios.get(`${API}/authors/featured?limit=1`),
      ]);
      setRows(digest.data?.rows || []);
      setPinned(featured.data?.featured?.pen_slug || '');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load rankings.');
    } finally {
      setBusy(false);
    }
  }, [days]);

  useEffect(() => { load(); }, [load]);

  const rotate = async (dryRun) => {
    setRotating(true);
    try {
      // Ops-triggered rotation uses the same cron endpoint; secret comes
      // from window prompt to keep it out of localStorage.
      const secret = await openPrompt({
        title: 'Cron secret required',
        description: 'Paste FEATURED_ROTATION_CRON_SECRET to trigger rotation.',
        placeholder: 'FEATURED_ROTATION_CRON_SECRET',
        confirmLabel: 'Run',
      });
      if (!secret) { setRotating(false); return; }
      const r = await axios.post(`${API}/internal/featured-author-rotation/tick?days=${days}&dry_run=${dryRun}`, {}, {
        headers: { 'X-Cron-Secret': secret },
      });
      const { picked, previous, changed } = r.data || {};
      toast.success(
        dryRun
          ? `Dry-run: would pick "${picked || 'nobody'}" (was "${previous || 'none'}")`
          : changed
            ? `Rotated: "${previous || 'none'}" → "${picked}"`
            : `No change — "${picked || 'nobody'}" already featured`
      );
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Rotation failed.');
    } finally {
      setRotating(false);
    }
  };

  return (
    <div className="space-y-3" data-testid="admin-author-rankings-panel">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="inline-flex items-center gap-2 text-[15px] font-semibold">
            <Award className="h-4 w-4 text-primary" /> Author rankings
          </h2>
          <p className="text-[11.5px] text-muted-foreground">
            Last {days}-day ticket-rail clicks + author-page views. Engagement
            score = <code>3 × clicks + views</code>. The winner drives the
            weekly featured-author rotation.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className="rounded-sm border bg-background px-2 py-1 text-[12px]"
            data-testid="rankings-days-select"
          >
            <option value={1}>Last 24 hours</option>
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={60}>Last 60 days</option>
          </select>
          <Button size="sm" variant="ghost" onClick={load} className="gap-1.5" disabled={busy}
                  data-testid="rankings-reload">
            <RefreshCcw className="h-3.5 w-3.5" /> Reload
          </Button>
          <Button size="sm" variant="outline" onClick={() => rotate(true)} className="gap-1.5" disabled={rotating}
                  data-testid="rankings-dry-run">
            <Play className="h-3.5 w-3.5" /> Dry-run
          </Button>
          <Button size="sm" onClick={() => rotate(false)} className="gap-1.5" disabled={rotating}
                  data-testid="rankings-rotate">
            {rotating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Award className="h-3.5 w-3.5" />}
            Rotate now
          </Button>
        </div>
      </div>

      {busy && rows.length === 0 ? (
        <div className="flex h-40 items-center justify-center text-sm text-muted-foreground">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading rankings…
        </div>
      ) : rows.length === 0 ? (
        <div className="flex h-40 items-center justify-center text-sm text-muted-foreground">
          No engagement yet in this window. Wait for readers to click a ticket.
        </div>
      ) : (
        <div className="overflow-hidden rounded-md border">
          <table className="w-full text-[12.5px]">
            <thead className="bg-muted/50">
              <tr>
                <th className="px-3 py-2 text-left">#</th>
                <th className="px-3 py-2 text-left">Author</th>
                <th className="px-3 py-2 text-right">Clicks</th>
                <th className="px-3 py-2 text-right">Views</th>
                <th className="px-3 py-2 text-right">Engagement</th>
                <th className="px-3 py-2 text-center">Featured</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={r.pen_slug} className="border-t">
                  <td className="px-3 py-2 text-muted-foreground">{i + 1}</td>
                  <td className="px-3 py-2 font-medium">
                    <a href={`/authors/${r.pen_slug}`} className="hover:underline">
                      {r.pen_name || r.pen_slug}
                    </a>
                    <div className="text-[10.5px] text-muted-foreground">/authors/{r.pen_slug}</div>
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">{r.clicks}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{r.views}</td>
                  <td className="px-3 py-2 text-right font-semibold tabular-nums">{r.engagement}</td>
                  <td className="px-3 py-2 text-center">
                    {pinned === r.pen_slug && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-[10.5px] font-semibold text-primary">
                        <Award className="h-3 w-3" /> Live
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
